import { useState } from 'react';
import { comments } from '@/mocks/home';
import Reveal from '@/components/base/Reveal';

const avatarStyle: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-800',
};

const hotTopics = [
  { label: '起因那段是否讲全了', count: 62 },
  { label: '06-27 节点的意义', count: 48 },
  { label: '后续还会有变化吗', count: 35 },
];

export default function DiscussionSection() {
  const [liked, setLiked] = useState<number[]>([]);
  const [draft, setDraft] = useState('');

  const toggleLike = (index: number) => {
    setLiked((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <section id="discussion" className="w-full bg-background-50">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-[1.35fr_0.65fr] lg:gap-10">
        <div>
          <span className="text-xs font-medium tracking-[0.2em] text-primary-600">
            DISCUSSION
          </span>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            读者讨论区
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            看看其他人读完时间线后的关注点，也欢迎留下你对海角黑料事件的看法。
          </p>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-200 font-heading text-sm font-bold text-secondary-800">
                我
              </span>
              <div className="flex-1">
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value.slice(0, 200))}
                  rows={3}
                  placeholder="说说你读完时间线后的想法……"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-50 p-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-foreground-400">{draft.length}/200</span>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    <i className="ri-send-plane-fill" />
                    发表看法
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {comments.map((comment, index) => {
              const isLiked = liked.includes(index);
              return (
                <Reveal key={comment.name} delay={index * 60}>
                  <article className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                    <div className="flex gap-3">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${
                          avatarStyle[comment.accent] ?? avatarStyle.secondary
                        }`}
                      >
                        {comment.initial}
                      </span>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-sm font-medium text-foreground-900">
                            {comment.name}
                          </span>
                          <span className="text-xs text-foreground-400">{comment.time}</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                          {comment.text}
                        </p>
                        <div className="mt-3 flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => toggleLike(index)}
                            className={`flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${
                              isLiked ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                            }`}
                          >
                            <i className={isLiked ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                            {comment.likes + (isLiked ? 1 : 0)}
                          </button>
                          <button
                            type="button"
                            className="flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-primary-600 cursor-pointer"
                          >
                            <i className="ri-chat-3-line" />
                            回复
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-fire-line text-primary-500" />
              讨论热点
            </h3>
            <ul className="mt-4 space-y-3">
              {hotTopics.map((topic) => (
                <li
                  key={topic.label}
                  className="flex items-center justify-between gap-3 border-b border-background-200 pb-3 last:border-b-0 last:pb-0"
                >
                  <span className="text-sm text-foreground-700">{topic.label}</span>
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-background-50 px-2 py-0.5 text-xs font-medium text-foreground-500">
                    {topic.count}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="font-heading text-base font-bold text-foreground-950">阅读提示</h3>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              讨论区用于交流阅读感受，内容仅代表网友个人观点，不代表本站立场。
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}